'use client';
import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────
interface EarningsSummary {
  totalEarned: number;
  completedSessions: number;
  pendingPayout: number;
  availableBalance: number;
  thisMonthEarned: number;
  lastMonthEarned: number;
}

interface PayoutRecord {
  id: string;
  amount: number;
  fee: number;
  netAmount: number;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  referenceNumber: string | null;
  requestedAt: string;
  processedAt: string | null;
  bankName?: string;
}

interface SessionEarning {
  id: string;
  clientName: string;
  serviceTitle: string;
  date: string;
  gross: number;
  platformFee: number;
  net: number;
  paymentStatus: string;
}

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string; dot: string }> = {
  pending:    { label: 'Pending',    bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-400' },
  processing: { label: 'Processing', bg: 'bg-blue-50',    text: 'text-blue-700',    dot: 'bg-blue-400' },
  completed:  { label: 'Completed',  bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  failed:     { label: 'Failed',     bg: 'bg-red-50',     text: 'text-red-600',     dot: 'bg-red-400' },
  cancelled:  { label: 'Cancelled',  bg: 'bg-slate-100',  text: 'text-slate-500',   dot: 'bg-slate-400' },
};

// ─── Sub-components ────────────────────────────────────────────────────────────
function SummaryCard({
  icon,
  label,
  value,
  sub,
  accentClass,
  iconClass,
}: {
  icon: string;
  label: string;
  value: string;
  sub?: string;
  accentClass?: string;
  iconClass?: string;
}) {
  return (
    <div className="bg-card border border-border rounded-2xl p-5 flex items-start gap-4">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${accentClass ?? 'bg-primary/10'}`}>
        <Icon
          name={icon as Parameters<typeof Icon>[0]['name']}
          size={22}
          className={iconClass ?? 'text-primary'}
        />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground font-500 uppercase tracking-wide mb-0.5">{label}</p>
        <p className="text-2xl font-700 text-foreground leading-tight">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-600 ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function SkeletonRow() {
  return (
    <div className="flex items-center gap-4 px-5 py-4 border-b border-border last:border-0 animate-pulse">
      <div className="w-9 h-9 rounded-full bg-muted flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-3 bg-muted rounded w-1/3" />
        <div className="h-2.5 bg-muted rounded w-1/4" />
      </div>
      <div className="h-3 bg-muted rounded w-16" />
    </div>
  );
}

function EmptyState({ icon, message }: { icon: string; message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-14 gap-3 text-muted-foreground">
      <Icon name={icon as Parameters<typeof Icon>[0]['name']} size={40} className="opacity-30" />
      <p className="text-sm font-500">{message}</p>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function ConsultantEarningsPage() {
  const supabase = createClient();
  const [activeTab, setActiveTab] = useState<'sessions' | 'payouts'>('sessions');
  const [summary, setSummary] = useState<EarningsSummary>({
    totalEarned: 0,
    completedSessions: 0,
    pendingPayout: 0,
    availableBalance: 0,
    thisMonthEarned: 0,
    lastMonthEarned: 0,
  });
  const [sessionEarnings, setSessionEarnings] = useState<SessionEarning[]>([]);
  const [payoutHistory, setPayoutHistory] = useState<PayoutRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      // ── Fetch wallet summary ──────────────────────────────────────────────
      const { data: walletData } = await supabase
        .from('consultant_wallets')
        .select('total_earned_usd, balance_usd, pending_usd')
        .eq('consultant_id', user.id)
        .maybeSingle();

      // ── Fetch completed/paid bookings as provider ─────────────────────────
      const { data: bookingData, error: bookingErr } = await supabase
        .from('consultation_bookings')
        .select('id, client_name, service_title, booking_date, amount_usd, payment_status, status')
        .eq('provider_user_id', user.id)
        .order('booking_date', { ascending: false });

      if (bookingErr) throw new Error(bookingErr.message);

      const allBookings = bookingData ?? [];
      const paidBookings = allBookings.filter(
        (b) => b.payment_status === 'paid' || b.status === 'completed'
      );

      // Build session earnings rows
      const sessions: SessionEarning[] = paidBookings.map((b) => {
        const gross = parseFloat(b.amount_usd ?? 0);
        const platformFee = parseFloat((gross * 0.1).toFixed(2));
        const net = parseFloat((gross - platformFee).toFixed(2));
        return {
          id: b.id,
          clientName: b.client_name ?? 'Unknown Client',
          serviceTitle: b.service_title ?? 'Consultation',
          date: b.booking_date ?? '',
          gross,
          platformFee,
          net,
          paymentStatus: b.payment_status ?? 'unpaid',
        };
      });
      setSessionEarnings(sessions);

      // Compute monthly earnings
      const now = new Date();
      const thisMonth = now.getMonth();
      const thisYear = now.getFullYear();
      const lastMonth = thisMonth === 0 ? 11 : thisMonth - 1;
      const lastMonthYear = thisMonth === 0 ? thisYear - 1 : thisYear;

      const thisMonthEarned = sessions
        .filter((s) => {
          if (!s.date) return false;
          const d = new Date(s.date);
          return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
        })
        .reduce((acc, s) => acc + s.net, 0);

      const lastMonthEarned = sessions
        .filter((s) => {
          if (!s.date) return false;
          const d = new Date(s.date);
          return d.getMonth() === lastMonth && d.getFullYear() === lastMonthYear;
        })
        .reduce((acc, s) => acc + s.net, 0);

      // Compute totals (prefer wallet data, fall back to computed)
      const totalEarned = walletData?.total_earned_usd
        ? parseFloat(walletData.total_earned_usd)
        : sessions.reduce((acc, s) => acc + s.net, 0);

      const availableBalance = walletData?.balance_usd
        ? parseFloat(walletData.balance_usd)
        : 0;

      const pendingPayout = walletData?.pending_usd
        ? parseFloat(walletData.pending_usd)
        : sessions
            .filter((s) => s.paymentStatus === 'pending')
            .reduce((acc, s) => acc + s.net, 0);

      setSummary({
        totalEarned,
        completedSessions: paidBookings.length,
        pendingPayout,
        availableBalance,
        thisMonthEarned,
        lastMonthEarned,
      });

      // ── Fetch payout / withdrawal history ────────────────────────────────
      const { data: withdrawalData, error: withdrawalErr } = await supabase
        .from('consultant_withdrawal_requests')
        .select(`
          id,
          amount_usd,
          fee_usd,
          net_amount_usd,
          withdrawal_status,
          reference_number,
          requested_at,
          processed_at,
          bank_account_id
        `)
        .eq('user_id', user.id)
        .order('requested_at', { ascending: false });

      if (withdrawalErr) throw new Error(withdrawalErr.message);

      // Fetch bank account names for display
      const bankAccountIds = [
        ...new Set((withdrawalData ?? []).map((w) => w.bank_account_id).filter(Boolean)),
      ];

      let bankMap: Record<string, string> = {};
      if (bankAccountIds.length > 0) {
        const { data: bankData } = await supabase
          .from('consultant_bank_accounts')
          .select('id, bank_name')
          .in('id', bankAccountIds);
        bankMap = Object.fromEntries((bankData ?? []).map((b) => [b.id, b.bank_name]));
      }

      const payouts: PayoutRecord[] = (withdrawalData ?? []).map((w) => ({
        id: w.id,
        amount: parseFloat(w.amount_usd ?? 0),
        fee: parseFloat(w.fee_usd ?? 0),
        netAmount: parseFloat(w.net_amount_usd ?? 0),
        status: w.withdrawal_status ?? 'pending',
        referenceNumber: w.reference_number ?? null,
        requestedAt: w.requested_at ?? '',
        processedAt: w.processed_at ?? null,
        bankName: w.bank_account_id ? bankMap[w.bank_account_id] : undefined,
      }));
      setPayoutHistory(payouts);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load earnings data');
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fmt = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n);

  const fmtDate = (iso: string) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const monthDiff =
    summary.lastMonthEarned > 0
      ? (((summary.thisMonthEarned - summary.lastMonthEarned) / summary.lastMonthEarned) * 100).toFixed(1)
      : null;

  return (
    <AppLayout activeRoute="/consultant-earnings">
      <div className="min-h-screen bg-background">
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="border-b border-border bg-card px-6 py-5">
          <div className="max-w-5xl mx-auto flex items-center justify-between gap-4">
            <div>
              <h1 className="text-xl font-700 text-foreground">Earnings Overview</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                Track your income, completed sessions, and payout history
              </p>
            </div>
            <button
              onClick={fetchData}
              className="flex items-center gap-2 px-4 py-2 rounded-xl border border-border text-sm font-500 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              <Icon name="ArrowPathIcon" size={16} />
              Refresh
            </button>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-6 py-6 space-y-6">
          {/* ── Error Banner ─────────────────────────────────────────────── */}
          {error && (
            <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
              <Icon name="ExclamationCircleIcon" size={18} className="text-red-500 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* ── Summary Cards ─────────────────────────────────────────────── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <SummaryCard
              icon="CurrencyDollarIcon"
              label="Total Earned"
              value={loading ? '—' : fmt(summary.totalEarned)}
              sub="All-time net earnings"
              accentClass="bg-emerald-100"
              iconClass="text-emerald-600"
            />
            <SummaryCard
              icon="CheckCircleIcon"
              label="Completed Sessions"
              value={loading ? '—' : summary.completedSessions.toString()}
              sub="Paid sessions delivered"
              accentClass="bg-primary/10"
              iconClass="text-primary"
            />
            <SummaryCard
              icon="ClockIcon"
              label="Pending Payout"
              value={loading ? '—' : fmt(summary.pendingPayout)}
              sub="Awaiting disbursement"
              accentClass="bg-amber-100"
              iconClass="text-amber-600"
            />
            <SummaryCard
              icon="BanknotesIcon"
              label="Available Balance"
              value={loading ? '—' : fmt(summary.availableBalance)}
              sub="Ready to withdraw"
              accentClass="bg-violet-100"
              iconClass="text-violet-600"
            />
          </div>

          {/* ── This Month vs Last Month ──────────────────────────────────── */}
          <div className="bg-card border border-border rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-600 text-foreground">Monthly Comparison</h2>
              {monthDiff !== null && !loading && (
                <span
                  className={`inline-flex items-center gap-1 text-xs font-600 px-2.5 py-1 rounded-full ${
                    parseFloat(monthDiff) >= 0
                      ? 'bg-emerald-50 text-emerald-700' :'bg-red-50 text-red-600'
                  }`}
                >
                  <Icon
                    name={parseFloat(monthDiff) >= 0 ? 'ArrowTrendingUpIcon' : 'ArrowTrendingDownIcon'}
                    size={13}
                  />
                  {parseFloat(monthDiff) >= 0 ? '+' : ''}{monthDiff}% vs last month
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-muted/40 rounded-xl p-4">
                <p className="text-xs text-muted-foreground font-500 mb-1">This Month</p>
                <p className="text-xl font-700 text-foreground">
                  {loading ? '—' : fmt(summary.thisMonthEarned)}
                </p>
              </div>
              <div className="bg-muted/40 rounded-xl p-4">
                <p className="text-xs text-muted-foreground font-500 mb-1">Last Month</p>
                <p className="text-xl font-700 text-foreground">
                  {loading ? '—' : fmt(summary.lastMonthEarned)}
                </p>
              </div>
            </div>
          </div>

          {/* ── Tabs ─────────────────────────────────────────────────────── */}
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            {/* Tab Bar */}
            <div className="flex border-b border-border">
              {(['sessions', 'payouts'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3.5 text-sm font-600 transition-colors ${
                    activeTab === tab
                      ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  <Icon
                    name={tab === 'sessions' ? 'CalendarDaysIcon' : 'ArrowDownTrayIcon'}
                    size={16}
                  />
                  {tab === 'sessions' ? 'Session Earnings' : 'Payout History'}
                  <span className="ml-1 bg-muted text-muted-foreground text-xs px-1.5 py-0.5 rounded-full">
                    {tab === 'sessions' ? sessionEarnings.length : payoutHistory.length}
                  </span>
                </button>
              ))}
            </div>

            {/* ── Session Earnings Tab ──────────────────────────────────── */}
            {activeTab === 'sessions' && (
              <div>
                {loading ? (
                  Array.from({ length: 4 }).map((_, i) => <SkeletonRow key={i} />)
                ) : sessionEarnings.length === 0 ? (
                  <EmptyState
                    icon="CurrencyDollarIcon"
                    message="No completed session earnings yet"
                  />
                ) : (
                  <>
                    {/* Table Header */}
                    <div className="hidden md:grid grid-cols-[1fr_120px_90px_90px_90px] gap-4 px-5 py-3 bg-muted/30 border-b border-border text-xs font-600 text-muted-foreground uppercase tracking-wide">
                      <span>Session</span>
                      <span>Date</span>
                      <span className="text-right">Gross</span>
                      <span className="text-right">Fee (10%)</span>
                      <span className="text-right">Net</span>
                    </div>
                    {sessionEarnings.map((s) => {
                      const initials = s.clientName
                        .split(' ')
                        .slice(0, 2)
                        .map((w) => w[0] ?? '')
                        .join('')
                        .toUpperCase();
                      return (
                        <div
                          key={s.id}
                          className="flex flex-col md:grid md:grid-cols-[1fr_120px_90px_90px_90px] gap-2 md:gap-4 px-5 py-4 border-b border-border last:border-0 hover:bg-muted/20 transition-colors"
                        >
                          {/* Client + service */}
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-9 h-9 rounded-full gradient-teal flex items-center justify-center text-white text-xs font-600 flex-shrink-0">
                              {initials}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-600 text-foreground truncate">{s.clientName}</p>
                              <p className="text-xs text-muted-foreground truncate">{s.serviceTitle}</p>
                            </div>
                          </div>
                          {/* Date */}
                          <div className="flex items-center md:block">
                            <span className="text-xs text-muted-foreground md:hidden mr-2">Date:</span>
                            <span className="text-sm text-muted-foreground">{fmtDate(s.date)}</span>
                          </div>
                          {/* Gross */}
                          <div className="flex items-center justify-between md:justify-end">
                            <span className="text-xs text-muted-foreground md:hidden">Gross</span>
                            <span className="text-sm text-foreground font-500">{fmt(s.gross)}</span>
                          </div>
                          {/* Fee */}
                          <div className="flex items-center justify-between md:justify-end">
                            <span className="text-xs text-muted-foreground md:hidden">Fee</span>
                            <span className="text-sm text-red-500">−{fmt(s.platformFee)}</span>
                          </div>
                          {/* Net */}
                          <div className="flex items-center justify-between md:justify-end">
                            <span className="text-xs text-muted-foreground md:hidden">Net</span>
                            <span className="text-sm font-700 text-emerald-600">{fmt(s.net)}</span>
                          </div>
                        </div>
                      );
                    })}
                    {/* Footer total */}
                    <div className="hidden md:grid grid-cols-[1fr_120px_90px_90px_90px] gap-4 px-5 py-3.5 bg-muted/30 border-t border-border text-sm font-700">
                      <span className="text-foreground">Total</span>
                      <span />
                      <span className="text-right text-foreground">
                        {fmt(sessionEarnings.reduce((a, s) => a + s.gross, 0))}
                      </span>
                      <span className="text-right text-red-500">
                        −{fmt(sessionEarnings.reduce((a, s) => a + s.platformFee, 0))}
                      </span>
                      <span className="text-right text-emerald-600">
                        {fmt(sessionEarnings.reduce((a, s) => a + s.net, 0))}
                      </span>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── Payout History Tab ────────────────────────────────────── */}
            {activeTab === 'payouts' && (
              <div>
                {loading ? (
                  Array.from({ length: 4 }).map((_, i) => <SkeletonRow key={i} />)
                ) : payoutHistory.length === 0 ? (
                  <EmptyState
                    icon="ArrowDownTrayIcon"
                    message="No payout requests found"
                  />
                ) : (
                  payoutHistory.map((p) => (
                    <div
                      key={p.id}
                      className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-4 border-b border-border last:border-0 hover:bg-muted/20 transition-colors"
                    >
                      {/* Icon */}
                      <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center flex-shrink-0">
                        <Icon name="ArrowDownTrayIcon" size={18} className="text-violet-600" />
                      </div>
                      {/* Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-600 text-foreground">
                            Withdrawal {p.referenceNumber ? `· ${p.referenceNumber}` : ''}
                          </p>
                          <StatusBadge status={p.status} />
                        </div>
                        <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                          {p.bankName && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Icon name="BuildingLibraryIcon" size={12} />
                              {p.bankName}
                            </span>
                          )}
                          <span className="text-xs text-muted-foreground">
                            Requested {fmtDate(p.requestedAt)}
                          </span>
                          {p.processedAt && (
                            <span className="text-xs text-muted-foreground">
                              · Processed {fmtDate(p.processedAt)}
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Amounts */}
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-700 text-foreground">{fmt(p.netAmount)}</p>
                        {p.fee > 0 && (
                          <p className="text-xs text-muted-foreground">
                            Fee: {fmt(p.fee)}
                          </p>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
